/**
 * Helper for turning a 403 (Forbidden) API error into a clear, role-aware message.
 *
 * Background: the backend enriches permission-based 403s with structured role context inside the ABP
 * error payload — `error.body.error.data.requiredRoles` (the tenant role(s) that grant the blocked
 * action, ordered least-privileged first) and `error.body.error.data.requiredPermission`. When that
 * context is present we tell the user exactly which role to request from their admin. When it is absent
 * (an endpoint we didn't enrich, or a host-only permission) we fall back to the backend message or a
 * generic "contact your admin" sentence.
 *
 * Modeled on {@link resolveSchedulingErrorMessage} in `bookingErrorMessage.ts`.
 */

/** Enriched-forbidden error code emitted by the backend (see ForbiddenWithRoleException). */
const FORBIDDEN_CODE = 'Routemize:Authorization:Forbidden';

const GENERIC_FORBIDDEN_MESSAGE =
  "You don't have permission to do this. Contact your administrator to request access.";

/**
 * Format the granting-role list for display. The backend already sends display-ready system role names
 * ("Super User", "Scheduling Assistant", "Team Member"), ordered least-privileged first, so we lead with
 * the smallest sufficient role and mention alternatives when present.
 */
function formatRoles(roles: string[]): string {
  const cleaned = roles.map((r) => (r ?? '').trim()).filter(Boolean);
  if (cleaned.length === 0) return '';
  if (cleaned.length === 1) return cleaned[0];
  // Lead with the least-privileged role; note the rest as alternatives.
  const [primary, ...rest] = cleaned;
  return `${primary} (or ${rest.join(' / ')})`;
}

/**
 * Resolve a role-aware message for a Forbidden (403) error.
 *
 * Returns `null` when the error is not a 403 so callers can keep their existing non-403 handling:
 * `const forbidden = resolveForbiddenMessage(err); toast.error(forbidden ?? fallback)`.
 *
 * Precedence for 403s:
 *  1. Structured `requiredRoles` from the backend → "This action requires the {role} role…".
 *  2. A meaningful backend message (e.g. our localized role-aware sentence, or another 403 message).
 *  3. A generic "contact your admin" fallback.
 */
export function resolveForbiddenMessage(error: any): string | null {
  const status: number | undefined = error?.status ?? error?.response?.status;
  if (status !== 403) {
    return null;
  }

  const apiError = error?.body?.error ?? error?.error ?? error?.response?.data?.error;
  const data = apiError?.data as Record<string, any> | undefined;
  const message: string | undefined = apiError?.message;

  const rawRoles = data?.requiredRoles;
  const roles: string[] = Array.isArray(rawRoles)
    ? rawRoles.filter((r): r is string => typeof r === 'string')
    : [];

  if (roles.length > 0) {
    const friendly = formatRoles(roles);
    return `You don't have permission to do this. It requires the ${friendly} role — contact your administrator to request access.`;
  }

  // No structured role context. Prefer a meaningful backend message (including our own localized
  // fallback sentence), otherwise use the generic copy. Never surface an empty string.
  if (apiError?.code === FORBIDDEN_CODE && message) {
    return message;
  }
  if (message && message.trim().length > 0) {
    return message;
  }

  return GENERIC_FORBIDDEN_MESSAGE;
}
